import React from 'react';
import styled from 'styled-components';
import { Form, Formik, FormikHelpers } from 'formik';
import {useTranslations} from "use-intl";

import { Nullable } from '@tager/web-core';

import { TextInputFormik } from '@/components/TextInput';
import Button from '@/components/Button';
import {
  areAllErrorsVisible,
  validatePhone,
} from '@/components/TextInput/formikHelpers/form.helpers';
import { CheaperFormPayload, sendCheaperForm } from '@/services/requests';
import UseModalsOpen from '@/hooks/useModalsOpen';
import { ProductCardFormType } from '@/components/ProductCard/ProductCardForm';
import {TFunction} from "@/typings/common";

const hasFrontendValidationErrors = (t:TFunction, values: CheaperFormPayload) =>
  !!Object.keys(validate(t, values)).length;

function validate(t:TFunction, { phone, name, price, shop }: CheaperFormPayload) {
  const errors: Partial<CheaperFormPayload> = {};
  if (!validatePhone(phone)) {
    errors.phone = t('forms.phone_error');
  }
  if (!phone) {
    errors.phone = t('forms.required');
  }
  if (!name) {
    errors.name = t('forms.required');
  }
  if (!price) {
    errors.price = t('forms.required');
  }
  if (!shop) {
    errors.shop = t('forms.required');
  }
  return errors;
}

type Props = {
  product: Nullable<ProductCardFormType & { id: number }>;
  setFormClosed: () => void;
};

function CheaperFormRequest({ setFormClosed, product }: Props) {
  const { closeModalState } = UseModalsOpen();
  const t = useTranslations();

  function handleSubmit(
    values: CheaperFormPayload,
    formikHelpers: FormikHelpers<CheaperFormPayload>
  ) {
    return sendCheaperForm(values)
      .then(() => {
        setFormClosed();
        closeModalState();
        formikHelpers.setSubmitting(true);
      })
      .catch(() => {
        formikHelpers.setErrors({
          phone: t('forms.unknown_error'),
        });
        formikHelpers.setStatus({
          hasBackendError: true,
        });
      })
      .finally(() => {
        formikHelpers.setSubmitting(false);
      });
  }

  return (
    <Component>
      <Formik<CheaperFormPayload>
        initialValues={{
          productId: product?.id as number,
          option: product?.optionName
            ? product?.optionName + ': ' + product?.optionValue
            : '',
          name: '',
          phone: '',
          price: '',
          shop: '',
        }}
        onSubmit={handleSubmit}
        validate={fields => validate(t, fields)}
      >
        {({ isSubmitting, isValid, values, dirty, ...props }) => (
          <Form>
            <FormRow>
              <TextInputFormik
                name="name"
                icon="profile"
                placeholder={t('forms.name')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>
            <FormRow>
              <TextInputFormik
                name="phone"
                icon="phone"
                placeholder={t('forms.phone')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>
            <FormRow>
              <TextInputFormik
                name="price"
                placeholder={t('forms.price_other')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>

            <FormRow>
              <TextInputFormik
                name="shop"
                placeholder={t('forms.shop')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>
            <Text>
              {t('forms.check')}
            </Text>
            <Button
              type="submit"
              variant="contained-primary-w100-f13"
              disabled={
                !(isValid || !hasFrontendValidationErrors(t, values)) &&
                areAllErrorsVisible(props.errors, props.touched)
              }
              children={t('forms.send')}
              loading={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </Component>
  );
}

export default CheaperFormRequest;

const Component = styled.div``;
const FormRow = styled.div`
  margin-bottom: 30px;
`;

const Text = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 20px;
`;
